// Credit Guard — global add-to-cart + checkout gate for the storefront widgets.
//
// Exposes window.PFCreditGuard.guard() → Promise<boolean>. Add-to-cart handlers
// call it FIRST: `true` means proceed, `false` means the visitor has no credit
// at the active branch and the guard has shown a modal (apply for credit /
// switch to a branch where they have credit). It FAILS OPEN — any error, or no
// credit data, resolves `true` so carts are never wrongly blocked.
//
// No target container: this is a global gate. init() wires the checkout click
// guard + on-load safety net and publishes window.PFCreditGuard (KEEP that
// global — add-to-cart handlers and the checkout flow depend on it). Widgets
// degrade gracefully if it's absent (they just proceed).

import { getTopDocument, esc } from '../shared/dom.js';
import { addedToCartToast } from '../shared/cartToast.js';
import { readCookie } from '../shared/identity.js';
import { apiJson } from '../shared/api.js';

// Widget-specific: the whole gate operates against the storefront TOP window
// (localStorage branch state, cookies, navigation), so keep a top-window helper.
function getTopWindow() { try { return window.top || window; } catch (e) { return window; } }

// The storefront's branch selector (storefront.branch-selector.js) is the sole
// owner of the current branch: it stores `SelectedBranchName` (+ numeric
// `SelectedBranchId`) and REMOVES them when nothing is selected ("Select store").
// We must read ONLY `SelectedBranchName` — the legacy `SelectedBranch` /
// `SelectedBranchCode` keys are never written by the storefront; a stale value
// left in them (e.g. "STRA") made an unregistered/`no branch` state look like a
// selected branch and defeated the credit gate. Empty name = no branch selected.
function activeBranch() {
	var name = '';
	try {
		name = getTopWindow().localStorage.getItem('SelectedBranchName') || '';
	} catch (e) {
		try { name = localStorage.getItem('SelectedBranchName') || ''; } catch (e2) { /* ignore */ }
	}
	return { name: (name || '').trim(), code: '' };
}

// GET /api/credit/status (cached once per page). Forwards the storefront's
// commerce_features/Session cookies as headers; apiJson also attaches the Bearer
// key (harmless — the endpoint accepts it) and the app base URL.
function loadCreditStatus() {
	if (window.__PF_CREDIT_STATUS_PROMISE__) return window.__PF_CREDIT_STATUS_PROMISE__;
	var headers = {};
	var jwt = readCookie('commerce_features');
	var sessionRef = readCookie('Session');
	if (jwt) headers['X-Commerce-Features'] = jwt;
	if (sessionRef) headers['X-Session-Reference'] = sessionRef;
	var p = apiJson('/api/credit/status', { headers: headers }, null);
	window.__PF_CREDIT_STATUS_PROMISE__ = p;
	return p;
}

// GET /api/branches → { codeToName, nameToCode } (uppercase keys). Best-effort.
function loadBranchMaps() {
	if (window.__PF_CREDIT_BRANCHMAPS__) return Promise.resolve(window.__PF_CREDIT_BRANCHMAPS__);
	return apiJson('/api/branches', null, []).then(function (list) {
		var codeToName = {}, nameToCode = {};
		(Array.isArray(list) ? list : []).forEach(function (b) {
			var name = (b.branchName || b.name || '').trim();
			var code = (b.branchShortCode || b.branchCode || '').trim();
			if (code) codeToName[code.toUpperCase()] = name || code;
			if (name) nameToCode[name.toUpperCase()] = code || name;
		});
		var maps = { codeToName: codeToName, nameToCode: nameToCode };
		window.__PF_CREDIT_BRANCHMAPS__ = maps;
		return maps;
	});
}

function buildApplyHref(applyPath, code, name) {
	var origin;
	try { origin = getTopWindow().location.origin; } catch (e) { origin = window.location.origin; }
	// GW-467: fallback only (the server supplies applyPath via /api/credit).
	// /credit-request is the B2B credit-request page; the old
	// /account/credit-application default 404s on the storefront.
	var path = (applyPath || '/credit-request')
		.replace(/\{code\}/g, encodeURIComponent(code || ''))
		.replace(/\{name\}/g, encodeURIComponent(name || ''));
	return /^https?:\/\//i.test(path) ? path : (origin + path);
}

// Fuzzy branch-name key for matching the credit branch against the storefront's
// branch list (ignores case, spacing and punctuation differences).
function branchNameKey(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, ''); }

// Persist the selected branch EXACTLY the way the storefront's own
// branch-selector selectBranch() does: everything keys off the numeric
// StoreBranchId in the `SelectedBranchId` cookie (read by the server) +
// localStorage `SelectedBranchId`/`SelectedBranchName`, and a `branchSelected`
// event so other widgets refresh. (The old code set only `SelectedBranchName`
// plus the legacy `SelectedBranch`/`SelectedBranchCode` keys the storefront
// never reads — so the name changed but the server/other widgets kept the old
// branch.)
function persistSelectedBranch(id, name) {
	var win = getTopWindow();
	try {
		var ls = win.localStorage;
		ls.setItem('SelectedBranchId', String(id));
		if (name) ls.setItem('SelectedBranchName', name);
		// Clear the legacy keys so nothing reads a stale value.
		ls.removeItem('SelectedBranch');
		ls.removeItem('SelectedBranchCode');
	} catch (e) { /* ignore */ }
	try {
		win.document.cookie = 'SelectedBranchId=' + encodeURIComponent(String(id)) +
			'; path=/; max-age=' + (365 * 24 * 60 * 60) + '; SameSite=Lax';
	} catch (e) { /* ignore */ }
	try {
		var $ = win.jQuery || window.jQuery;
		if ($ && typeof $ === 'function') {
			$(win.document).trigger('branchSelected', [{ branchId: id, branchName: name || '' }]);
		}
	} catch (e) { /* ignore */ }
}

// Switch to the given branch. We only have the DMC code/name here, but the
// storefront needs the numeric StoreBranchId — resolve it from the storefront's
// /ajax/branches (matched by name) before persisting. Falls back to name-only
// if the id can't be resolved (degraded, but never worse than before).
function switchBranch(code, name) {
	var win = getTopWindow();
	var origin;
	try { origin = win.location.origin; } catch (e) { origin = window.location.origin; }
	var reload = function () { try { win.location.reload(); } catch (e) { window.location.reload(); } };
	var nameOnlyFallback = function () {
		try { win.localStorage.setItem('SelectedBranchName', name || ''); }
		catch (e) { try { localStorage.setItem('SelectedBranchName', name || ''); } catch (e2) { /* ignore */ } }
	};
	var target = branchNameKey(name);
	fetch(origin + '/ajax/branches', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
		.then(function (r) { return r.ok ? r.json() : null; })
		.then(function (data) {
			var list = (data && data.Branches) ? data.Branches : (Array.isArray(data) ? data : []);
			var match = null;
			for (var i = 0; i < list.length; i++) {
				var b = list[i];
				if (b && b.StoreBranchId != null && branchNameKey(b.Name) === target) { match = b; break; }
			}
			if (match) persistSelectedBranch(match.StoreBranchId, match.Name || name);
			else nameOnlyFallback();
			reload();
		})
		.catch(function () { nameOnlyFallback(); reload(); });
}

// True when a real customer account is signed in. Decoded from the
// commerce_features JWT's `acc` claim (acc > 0 = logged in) CLIENT-SIDE, so the
// login gate keeps working even if the credit-status API is unreachable. No
// cookie ⇒ logged out; an undecodable token ⇒ assume logged in (never wrongly block).
function isLoggedIn() {
	var jwt = readCookie('commerce_features');
	if (!jwt) return false;
	try {
		var parts = jwt.split('.');
		if (parts.length < 2) return true;
		var b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		while (b64.length % 4) b64 += '=';
		var claims = JSON.parse(atob(b64));
		return Number(claims && claims.acc) > 0;
	} catch (e) { return true; }
}

function loginUrl() {
	var origin, loc;
	try { origin = getTopWindow().location.origin; } catch (e) { origin = window.location.origin; }
	try { loc = getTopWindow().location; } catch (e) { loc = window.location; }
	// redirectTo must be a relative PATH (path + query), not the absolute URL.
	var here = loc.pathname + loc.search + loc.hash;
	return origin + '/account/login?redirectTo=' + encodeURIComponent(here);
}

function openBranchSelector() {
	var win = getTopWindow();
	try {
		if (win.SFUI && win.SFUI.branchSelector && typeof win.SFUI.branchSelector.openDrawer === 'function') {
			win.SFUI.branchSelector.openDrawer();
			return;
		}
	} catch (e) { /* ignore */ }
	try { var btn = getTopDocument().getElementById('fulfilment-selector-btn') || getTopDocument().getElementById('branch-selector-btn'); if (btn) btn.click(); } catch (e) { /* ignore */ }
}

function showModal(activeName, activeCode, applyPath, creditBranches) {
	var doc = getTopDocument();
	var body = doc.body || doc.getElementsByTagName('body')[0];

	var overlay = doc.createElement('div');
	overlay.className = 'pfcg-overlay';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');

	var branchLabel = activeName || 'this branch';
	var switchList = '';
	if (creditBranches && creditBranches.length > 0) {
		switchList += '<div class="pfcg-section-label">Switch to a branch where you have credit</div>';
		creditBranches.forEach(function (b, i) {
			switchList += '<button type="button" class="pfcg-branch-btn" data-idx="' + i + '">'
				+ '<span>' + esc(b.name || b.code) + '</span><span class="pfcg-arrow">Switch &rarr;</span></button>';
		});
	}

	overlay.innerHTML = ''
		+ '<div class="pfcg-card">'
		+ '  <div class="pfcg-head">'
		+ '    <span class="pfcg-badge"><svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg></span>'
		+ '    <div>'
		+ '      <h2 class="pfcg-title">Credit required</h2>'
		+ '      <p class="pfcg-sub">You don’t have a credit account at <strong>' + esc(branchLabel) + '</strong>. Apply for credit here, or switch to a branch where you already have credit.</p>'
		+ '    </div>'
		+ '  </div>'
		+ '  <div class="pfcg-body">'
		+ '    <a class="pfcg-btn pfcg-btn-primary" id="pfcg-apply" href="' + esc(buildApplyHref(applyPath, activeCode, activeName)) + '">'
		+ '      Apply for credit at ' + esc(branchLabel)
		+ '    </a>'
		+      switchList
		+ '  </div>'
		+ '  <div class="pfcg-foot"><button type="button" class="pfcg-cancel" id="pfcg-cancel">Not now</button></div>'
		+ '</div>';

	function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); doc.removeEventListener('keydown', onKey); }
	function onKey(e) { if (e.key === 'Escape') close(); }

	overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
	body.appendChild(overlay);
	doc.addEventListener('keydown', onKey);

	var cancel = overlay.querySelector('#pfcg-cancel');
	if (cancel) cancel.addEventListener('click', close);

	// Wire branch switch buttons
	overlay.querySelectorAll('.pfcg-branch-btn').forEach(function (btn) {
		btn.addEventListener('click', function () {
			var b = creditBranches[parseInt(btn.getAttribute('data-idx'), 10)];
			if (b) switchBranch(b.code, b.name);
		});
	});
}

// Generic single-action modal (reuses the pfcg-* styles). `href` renders a link;
// `onPrimary` renders a button that runs the callback. Plus a "Not now" dismiss.
function showActionModal(opts) {
	var doc = getTopDocument();
	var body = doc.body || doc.getElementsByTagName('body')[0];
	var overlay = doc.createElement('div');
	overlay.className = 'pfcg-overlay';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	var primary = opts.href
		? '<a class="pfcg-btn pfcg-btn-primary" id="pfcg-primary" href="' + esc(opts.href) + '">' + esc(opts.primaryLabel) + '</a>'
		: '<button type="button" class="pfcg-btn pfcg-btn-primary" id="pfcg-primary">' + esc(opts.primaryLabel) + '</button>';
	overlay.innerHTML = ''
		+ '<div class="pfcg-card">'
		+ '  <div class="pfcg-head">'
		+ '    <span class="pfcg-badge">' + (opts.iconSvg || '') + '</span>'
		+ '    <div><h2 class="pfcg-title">' + esc(opts.title) + '</h2><p class="pfcg-sub">' + esc(opts.sub) + '</p></div>'
		+ '  </div>'
		+ '  <div class="pfcg-body">' + primary + '</div>'
		+ '  <div class="pfcg-foot"><button type="button" class="pfcg-cancel" id="pfcg-cancel">Not now</button></div>'
		+ '</div>';
	function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); doc.removeEventListener('keydown', onKey); }
	function onKey(e) { if (e.key === 'Escape') close(); }
	overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
	body.appendChild(overlay);
	doc.addEventListener('keydown', onKey);
	var cancel = overlay.querySelector('#pfcg-cancel');
	if (cancel) cancel.addEventListener('click', close);
	var primaryEl = overlay.querySelector('#pfcg-primary');
	if (primaryEl && typeof opts.onPrimary === 'function') {
		primaryEl.addEventListener('click', function (e) { e.preventDefault(); close(); opts.onPrimary(); });
	}
}

var LOCK_ICON = '<svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path stroke-linecap="round" d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
var PIN_ICON = '<svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>';

function showLoginModal() {
	showActionModal({
		iconSvg: LOCK_ICON,
		title: 'Please sign in',
		sub: 'Sign in to your account to add items to your cart.',
		primaryLabel: 'Sign in',
		href: loginUrl()
	});
}

function showSelectBranchModal() {
	showActionModal({
		iconSvg: PIN_ICON,
		title: 'Select a branch',
		sub: 'Please choose your branch before adding items to your cart.',
		primaryLabel: 'Choose branch',
		onPrimary: openBranchSelector
	});
}

// Gated B2B visitor with NO branch selected: they can only buy at a branch where
// they hold credit, so let them pick one of those DIRECTLY here (one step). The
// old flow sent them to the generic branch drawer, where picking a no-credit
// branch got them blocked again and forced a second branch switch — the "jumping
// around". Reuses switchBranch (persist + reload).
function showChooseCreditBranchModal(creditBranches, applyPath) {
	var doc = getTopDocument();
	var body = doc.body || doc.getElementsByTagName('body')[0];
	var overlay = doc.createElement('div');
	overlay.className = 'pfcg-overlay';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	var list = '';
	creditBranches.forEach(function (b, i) {
		list += '<button type="button" class="pfcg-branch-btn" data-idx="' + i + '">'
			+ '<span>' + esc(b.name || b.code) + '</span><span class="pfcg-arrow">Select &rarr;</span></button>';
	});
	var applyLink = applyPath
		? '<a class="pfcg-cancel" style="display:block;margin-top:6px;text-align:center;" href="' + esc(buildApplyHref(applyPath, '', '')) + '">Apply for credit at another branch</a>'
		: '';
	overlay.innerHTML = ''
		+ '<div class="pfcg-card">'
		+ '  <div class="pfcg-head">'
		+ '    <span class="pfcg-badge">' + PIN_ICON + '</span>'
		+ '    <div>'
		+ '      <h2 class="pfcg-title">Select your branch</h2>'
		+ '      <p class="pfcg-sub">Choose the branch where you have a credit account to add items to your cart.</p>'
		+ '    </div>'
		+ '  </div>'
		+ '  <div class="pfcg-body">'
		+ '    <div class="pfcg-section-label">Your credit branch' + (creditBranches.length > 1 ? 'es' : '') + '</div>'
		+      list
		+      applyLink
		+ '  </div>'
		+ '  <div class="pfcg-foot"><button type="button" class="pfcg-cancel" id="pfcg-cancel">Not now</button></div>'
		+ '</div>';
	function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); doc.removeEventListener('keydown', onKey); }
	function onKey(e) { if (e.key === 'Escape') close(); }
	overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
	body.appendChild(overlay);
	doc.addEventListener('keydown', onKey);
	var cancel = overlay.querySelector('#pfcg-cancel');
	if (cancel) cancel.addEventListener('click', close);
	overlay.querySelectorAll('.pfcg-branch-btn').forEach(function (btn) {
		btn.addEventListener('click', function () {
			var b = creditBranches[parseInt(btn.getAttribute('data-idx'), 10)];
			if (b) switchBranch(b.code, b.name);
		});
	});
}

/**
 * Resolve the visitor's credit context for the branch they currently have
 * selected. Shared by guard() (the add-to-cart gate) and the stock widgets
 * (which hide per-branch stock when the selected branch has no credit).
 *
 * Resolves to:
 *   { gated, hasBranchSelected, activeName, activeCode, creditBranches[],
 *     hasCreditHere, applyPath }
 *   - gated: the visitor is an account user whose purchases are limited to
 *     specific credit branches (isAccountUser && hasCreditData && !allBranches).
 *   - hasCreditHere: the active branch is one of their credit branches.
 * Fails soft to a permissive context (gated:false) so nothing is wrongly
 * hidden or blocked.
 */
function getContext() {
	// Computed client-side (from the JWT), so it's correct even if the API fails.
	var anon = !isLoggedIn();
	return loadCreditStatus().then(function (status) {
		var gated = !!(status && status.isAccountUser && status.hasCreditData && !status.allBranches);
		var branches = (status && Array.isArray(status.creditBranches)) ? status.creditBranches : [];
		return loadBranchMaps().then(function (maps) {
			var act = activeBranch();
			// The storefront's localStorage can hold a `SelectedBranch` code that
			// DISAGREES with `SelectedBranchName` — e.g. the account's home/registered
			// branch code ("STRA"/Strand) while the shopper is actually VIEWING
			// "Pretoria East". The NAME is the branch they're looking at (it's what the
			// nav + product widget display), so resolve the code from the name via the
			// authoritative branch map and DON'T trust a stored code that contradicts it;
			// fall back to the stored code only when there's no name at all.
			var activeName = act.name || (act.code && maps.codeToName[act.code.toUpperCase()]) || act.code || '';
			var activeCode = (act.name
				? (maps.nameToCode[act.name.toUpperCase()] || '')
				: (act.code || '')).toUpperCase();
			var activeNameU = (activeName || '').toUpperCase();
			var enriched = branches.map(function (b) {
				var code = (b.code || '').toUpperCase();
				return { code: code, name: b.name || maps.codeToName[code] || b.code };
			});
			// Match registration on code OR name so a mislabelled/stale code on either
			// side can't wrongly grant (or deny) credit at the viewed branch.
			var hasCreditHere = enriched.some(function (b) {
				return (activeCode && b.code === activeCode) ||
					(activeNameU && (b.name || '').toUpperCase() === activeNameU);
			});
			return {
				gated: gated,
				isAnonymous: anon,
				hasBranchSelected: !!(activeCode || activeName),
				activeName: activeName,
				activeCode: activeCode,
				creditBranches: enriched,
				hasCreditHere: hasCreditHere,
				applyPath: status ? status.applyPath : ''
			};
		});
	}).catch(function () {
		// Keep the anon + branch signals (both client-side) meaningful on failure.
		var act = activeBranch();
		return { gated: false, isAnonymous: anon, hasBranchSelected: !!act.name, activeName: act.name || '', activeCode: '', creditBranches: [], hasCreditHere: false, applyPath: '' };
	});
}

// ── Pending add-to-cart (auto-complete after a branch/credit block) ─────────
// The branch/credit block reloads the page (switchBranch → location.reload), and
// the add the visitor clicked was never performed — so they had to click "Add to
// cart" AGAIN after picking a branch. Instead we remember what they were adding,
// survive the reload via sessionStorage, and finish the add automatically once a
// valid branch is active. One-shot + short TTL so a stale intent can't silently
// add later.
var PENDING_ADD_KEY = 'pfcg_pending_add';
var PENDING_ADD_TTL_MS = 5 * 60 * 1000;

function _stashPendingAdd(pending) {
	if (!pending || !pending.sku) return;
	try {
		sessionStorage.setItem(PENDING_ADD_KEY, JSON.stringify({
			sku: pending.sku,
			qty: pending.qty || 1,
			at: Date.now(),
		}));
	} catch (e) { /* ignore */ }
}

// POST the storefront add-to-cart (same endpoint every caller uses). Returns a
// Promise<boolean> of success; refreshes the mini-cart + toasts on success.
function _addToCartNow(sku, qty) {
	if (!sku) return Promise.resolve(false);
	qty = Math.max(1, parseInt(qty, 10) || 1);
	var win = getTopWindow();
	var origin; try { origin = win.location.origin; } catch (e) { origin = window.location.origin; }
	return fetch(origin + '/ajax/cart/add-item', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
		body: JSON.stringify({ Sku: sku, Qty: qty }),
		credentials: 'include',
	})
		.then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
		.then(function (res) {
			var success = res && res.data && (res.data.Success === true || res.data.success === true);
			var toastr = win.Utils && typeof win.Utils.Toastr === 'function' ? win.Utils.Toastr : null;
			if (res && res.ok && success) {
				if (win.Cart && win.Cart.MiniCart && typeof win.Cart.MiniCart.GetMiniCart === 'function') win.Cart.MiniCart.GetMiniCart();
				if (win.Cart && typeof win.Cart.UpdateCartCount === 'function') win.Cart.UpdateCartCount();
				if (toastr) toastr(addedToCartToast(null, 1));
				return true;
			}
			return false;
		})
		.catch(function () { return false; });
}

// On page load: if there's a stashed pending add and the context now ALLOWS it
// (logged in + branch + credit), finish the add so the visitor doesn't re-click.
// One-shot (cleared immediately); if still blocked we simply drop it (never
// re-open a modal on load).
function _replayPendingAdd() {
	var raw = null;
	try { raw = sessionStorage.getItem(PENDING_ADD_KEY); } catch (e) { return; }
	if (!raw) return;
	try { sessionStorage.removeItem(PENDING_ADD_KEY); } catch (e) { /* ignore */ }
	var pending;
	try { pending = JSON.parse(raw); } catch (e) { return; }
	if (!pending || !pending.sku) return;
	if (!pending.at || (Date.now() - pending.at) > PENDING_ADD_TTL_MS) return; // stale
	getContext().then(function (ctx) {
		var allowed = ctx && !ctx.isAnonymous && ctx.hasBranchSelected && (!ctx.gated || ctx.hasCreditHere);
		if (allowed) _addToCartNow(pending.sku, pending.qty);
	}).catch(function () { /* ignore */ });
}

/**
 * The add-to-cart gate. Resolves true when the visitor may add to cart, false
 * when a modal was shown instead. Fails open.
 *
 * Pass the item being added — `guard({ sku, qty })` — so that when the gate
 * blocks on branch/credit (which reloads the page after the visitor picks a
 * branch), the add is remembered and auto-completed on the next load. Callers
 * that omit it still work; they just don't get the auto-complete.
 */
function guard(pending) {
	return getContext().then(function (ctx) {
		// 1. Logged out → prompt to sign in first.
		if (ctx.isAnonymous) { showLoginModal(); return false; }
		// 2. Signed in but no branch chosen → prompt to select a branch first.
		//    A GATED B2B visitor can only buy at their credit branch(es), so steer
		//    them straight there in one step (avoids picking a no-credit branch and
		//    getting blocked again). Everyone else gets the normal branch drawer.
		if (!ctx.hasBranchSelected) {
			_stashPendingAdd(pending);
			if (ctx.gated && ctx.creditBranches && ctx.creditBranches.length > 0) {
				showChooseCreditBranchModal(ctx.creditBranches, ctx.applyPath);
			} else {
				showSelectBranchModal();
			}
			return false;
		}
		// 3. B2B credit gate: no credit at the selected branch → prompt to apply/switch.
		if (!ctx.gated) return true;
		if (ctx.hasCreditHere) return true;
		_stashPendingAdd(pending);
		showModal(ctx.activeName, ctx.activeCode, ctx.applyPath, ctx.creditBranches);
		return false;
	}).catch(function () { return true; }); // fail open on unexpected errors
}

// ── Checkout gate ─────────────────────────────────────────────────────────
// The add-to-cart guard only checks credit at the moment of adding. A B2B
// credit-limited visitor could add items at a branch where they DO have credit
// (e.g. Strand), then switch to a branch where they DON'T, and still reach
// checkout. So we re-validate the CURRENTLY selected branch's credit at
// checkout time too.

// Resolves { ok, ctx }. ok=false only for a gated B2B visitor who has a branch
// selected but no credit at it. Fails open (ok=true) on any error.
function checkoutAllowed() {
	return getContext().then(function (ctx) {
		var blocked = !!(ctx && !ctx.isAnonymous && ctx.gated && ctx.hasBranchSelected && !ctx.hasCreditHere);
		return { ok: !blocked, ctx: ctx };
	}).catch(function () { return { ok: true, ctx: null }; });
}

function _path() {
	try { return getTopWindow().location.pathname || ''; } catch (e) { return (window.location.pathname || ''); }
}
function _cartUrl() {
	var origin; try { origin = getTopWindow().location.origin; } catch (e) { origin = window.location.origin; }
	return origin + '/checkout/cart';
}
// The checkout FLOW (not the cart itself, which is fine to view).
function _isCheckoutFlowPath(p) {
	return /^\/checkout\/(details|shipping|delivery|payment|confirm|onepage|guest)/i.test(p || '');
}
// Does this element/href head into the checkout flow?
function _leadsToCheckout(el) {
	if (!el) return false;
	var href = (el.getAttribute && (el.getAttribute('href') || el.getAttribute('data-href'))) || '';
	if (/\/checkout\/(details|onepage|guest)/i.test(href)) return true;
	// The storefront's cart "Proceed to Checkout" is an <a> with NO href — it
	// navigates from an inline onclick (SFUI.cart.proceedToCheckout() →
	// window.location = '/checkout/details'). Catch it by that handler so we can
	// block cleanly on the cart page instead of letting it flash into the
	// checkout flow and relying on the on-load bounce.
	var onclick = (el.getAttribute && el.getAttribute('onclick')) || '';
	if (/proceedToCheckout/i.test(onclick)) return true;
	var txt = (el.textContent || '').trim().toLowerCase();
	return /\b(proceed|continue)\s+to\s+checkout\b/.test(txt);
}

// Intercept clicks that navigate into the checkout flow so we can block on the
// cart page (nicer than the redirect bounce below). Re-dispatches the click
// once credit is confirmed.
function installCheckoutClickGuard() {
	var doc = getTopDocument();
	doc.addEventListener('click', function (e) {
		// Include `a[onclick]` — the "Proceed to Checkout" anchor has an inline
		// onclick but NO href, so `a[href]` alone missed it and the guard never
		// fired (the click flashed into /checkout/details instead of blocking here).
		var el = (e.target && e.target.closest) ? e.target.closest('a[href],a[onclick],button,[data-href]') : null;
		if (!el || !_leadsToCheckout(el)) return;
		if (el.__pfcgAllow) { el.__pfcgAllow = false; return; } // allow the re-dispatched click
		e.preventDefault();
		e.stopPropagation();
		checkoutAllowed().then(function (res) {
			if (res.ok) { el.__pfcgAllow = true; el.click(); }
			else if (res.ctx) showModal(res.ctx.activeName, res.ctx.activeCode, res.ctx.applyPath, res.ctx.creditBranches);
		});
	}, true);
}

// Page-load safety net: if we somehow land ON the checkout flow at a no-credit
// branch (direct URL, in-flight branch switch), bounce back to the cart and
// show the credit modal there.
function enforceCheckoutOnLoad() {
	var path = _path();
	if (_isCheckoutFlowPath(path)) {
		checkoutAllowed().then(function (res) {
			if (res.ok || !res.ctx) return;
			try {
				sessionStorage.setItem('pfcg_checkout_block', JSON.stringify({
					name: res.ctx.activeName, code: res.ctx.activeCode,
					applyPath: res.ctx.applyPath, branches: res.ctx.creditBranches || []
				}));
			} catch (e) { /* ignore */ }
			try { getTopWindow().location.replace(_cartUrl()); } catch (e) { window.location.replace(_cartUrl()); }
		});
		return;
	}
	// Back on the cart after a bounce → explain why with the credit modal.
	if (/^\/checkout\/cart/i.test(path)) {
		var raw = null;
		try { raw = sessionStorage.getItem('pfcg_checkout_block'); } catch (e) { /* ignore */ }
		if (raw) {
			try { sessionStorage.removeItem('pfcg_checkout_block'); } catch (e) { /* ignore */ }
			try { var d = JSON.parse(raw); showModal(d.name, d.code, d.applyPath, d.branches || []); } catch (e) { /* ignore */ }
		}
	}
}

function initCheckoutGate() {
	try { installCheckoutClickGuard(); } catch (e) { /* ignore */ }
	try { enforceCheckoutOnLoad(); } catch (e) { /* ignore */ }
}

export function init() {
	// Global gate — no target container. Publish the API other add-to-cart
	// handlers depend on, then wire the checkout gate. Safe to call on any page.
	window.PFCreditGuard = { guard: guard, getContext: getContext, checkoutAllowed: checkoutAllowed };
	initCheckoutGate();
	// Finish an add the visitor started before a branch/credit block reloaded the page.
	try { _replayPendingAdd(); } catch (e) { /* ignore */ }
}

export var id = 'credit-guard';
